import type { Ctx } from 'boardgame.io';
import { debug } from '../utils/debug';
import type { BarbadosGameState, BarbadosPlayer, PlayerId } from '../types';
import { PLAYER_COLORS } from '../types';
import { BARBADOS_MAP_CONFIG } from '../../data/map/barbados/config';
import { loadBarbadosMap } from './utils/mapLoader';
import {
  initializeBarbadosCubes,
  initializeBarbadosGoodsDisplay,
  placeInitialGoodsOnMap
} from './utils/goodsSetup';

/**
 * Barbadosソロプレイ用のゲーム初期状態を生成
 * @param _ctx Boardgame.io コンテキスト（未使用）
 * @returns 初期ゲーム状態（BarbadosGameState）
 */
export function setupBarbadosGame(_ctx: Ctx): BarbadosGameState {
  // Barbados Solo Play: プレイヤーは常に1人（ID='0'）
  const playerId: PlayerId = '0';

  // Barbadosプレイヤー初期化
  const soloPlayer: BarbadosPlayer = {
    id: playerId,
    name: 'Solo Player',
    color: PLAYER_COLORS[0],     // プレイヤーカラー
    cash: 10,                    // 初期資金 $10
    incomeLevel: 0,              // 収入レベル 0
    sharesIssued: 2,             // 初期株式数 2（Barbados固有）
    locomotiveLevel: 1,          // 初期機関車レベル 1リンク
    selectedAction: null,        // 未選択
    playerOrder: 1,              // ソロプレイなので常に1
    isBankrupt: false,           // 倒産していない
    orderBidAmount: 0,           // Phase 2未使用（Barbadosではスキップ）
    tilesPlacedThisTurn: 0,      // Phase 4: 今ターン配置したタイル数
    hasPassedBuild: false,       // Phase 4: 手番終了フラグ
    buildTrackHistory: [],       // Phase 4: Cancel操作用の配置履歴
    hasUpgradedLocoThisTurn: false,  // Phase 5: 今ターン機関車強化済みフラグ
    goodsTransportedThisPhase: 0,    // Phase 5: 輸送回数
    turnsCompletedThisPhase: 0,  // Phase 5: 完了した手番数（0, 1, 2）
    goodsPlacedThisTurn: 0,      // Phase 9: 今ターン配置した商品数
    newCityTilesPlacedThisTurn: 0, // Phase 4: 今ターン配置した新都市タイル数

    // Barbados固有プロパティ
    currentTurnSharesIssued: 0,  // 今ターン発行した株式数（0 or 1）
    hasExecutedIssueSharesMove: false,  // 株式発行Move実行済みフラグ
    finalCash: null,             // 最終スコア（ゲーム終了前はnull）
    isVictorious: false          // 勝利フラグ
  };

  // プレイヤーマップ
  const players: Record<PlayerId, BarbadosPlayer> = {
    [playerId]: soloPlayer
  };

  // マップデータ読み込み（Barbados）
  let map = loadBarbadosMap();

  // 商品キューブ初期化（紫除外）
  let cubesInBag = initializeBarbadosCubes();

  // 商品補充表初期化（明側最上段のみ10個）
  const { goodsDisplay, remainingCubes } = initializeBarbadosGoodsDisplay(cubesInBag);
  cubesInBag = remainingCubes;

  // マップ上に初期商品配置（全6都市にランダムなキューブ1個ずつ）
  const { updatedMap, remainingCubes: finalCubes } = placeInitialGoodsOnMap(map, cubesInBag);
  map = updatedMap;
  cubesInBag = finalCubes;

  debug.log(`[setupBarbadosGame] Map loaded: ${map.length} hexes, ${cubesInBag.length} cubes in bag`);

  // Barbadosゲーム状態初期化
  return {
    mapName: 'Barbados',
    mapConfig: BARBADOS_MAP_CONFIG,
    players,
    map,
    goodsDisplay,
    cubesInBag,
    removedCubes: [],
    completedLinks: [],            // 完成区間なし
    unfinishedSections: [],        // 未完成区間なし
    ferryLinks: [],                // Barbados: フェリーリンクなし
    adjacentLinks: [],             // Barbados: 隣接リンクなし
    isGameOver: false,             // ゲーム継続中
    undoCount: 0,                  // Undo to Turn Start 実行回数
    endReason: null,
    finalScores: {},

    // Barbados固有プロパティ
    actionMarkers: {               // ★ アクションマーカーを初期化
      ENGINEER: false,
      LOCOMOTIVE: false,
      URBANIZATION: false,
      PRODUCTION: false
    },
    actionMarkerResetTurn: undefined,
    actualTurn: 0,                 // ゲームターン初期値は0（Phase 1で+1される）

    // 自動フェーズ用
    autoPhaseResult: null,         // 自動フェーズ処理結果
    completedAutoPhases: [],       // 完了した自動フェーズ履歴

    // Town Disk 管理
    townDisksRemaining: 8,         // Town Disk 残り枚数（初期値8）
    townDiskPlaced: {},            // 各 Town への配置記録

    // ML学習用
    moveLog: []                    // Move-Level Replay Log
  };
}
